import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Badge,
  ModalHeader,
  ModalHeading,
  ModalBody,
  ModalFooter,
  useOverlayState,
} from '@heroui/react'
import {
  Sun,
  Moon,
  LogOut,
  User,
  Settings,
  Shield,
  Crown,
  AlertTriangle,
  Bell,
  BookOpen,
  BarChart3,
  Server,
  ShoppingCart,
  Gift,
} from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeProvider'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import V2BoardAPI from '../../services/api'
import { getUserInfo } from '../../services/user'
import { getNotices } from '../../services/notice'
import MobileSheetModal from '../ui/MobileSheetModal'

const Header = () => {
  const { user, logout, updateUserData } = useUser()
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()

  const [isScrolled, setIsScrolled] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [noticeCount, setNoticeCount] = useState(0)
  const [openTicketCount, setOpenTicketCount] = useState(0)
  const {
    isOpen: isLogoutConfirmOpen,
    open: openLogoutConfirm,
    close: closeLogoutConfirm,
  } = useOverlayState()

  const handleLogout = useCallback(async () => {
    try {
      setIsLoggingOut(true)
      await logout()
      toast.success('已退出登录')
      closeLogoutConfirm()
      navigate('/login')
    } catch (error) {
      toast.error('退出登录失败，请重试')
    } finally {
      setIsLoggingOut(false)
    }
  }, [logout, navigate, closeLogoutConfirm])

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.token && user?.baseUrl && !user?.avatar_url) {
        try {
          const api = new V2BoardAPI(user.baseUrl, user.token)
          const userInfo = await getUserInfo(api)

          if (userInfo?.avatar_url) {
            updateUserData({ avatar_url: userInfo.avatar_url })
          }
        } catch (error) {
          console.error('Failed to fetch user avatar:', error)
        }
      }
    }

    fetchAvatar()
  }, [user?.token, user?.baseUrl, user?.avatar_url, updateUserData])

  useEffect(() => {
    if (!user?.token || !user?.baseUrl) return

    let cancelled = false
    const api = new V2BoardAPI(user.baseUrl, user.token)

    const fetchCounts = async () => {
      try {
        const notices = await getNotices(api)
        if (!cancelled) {
          const visible = Array.isArray(notices)
            ? notices.filter((n) => n?.show).length
            : 0
          setNoticeCount(visible)
        }
      } catch (e) {
        // silently ignore — header badges are non-critical
      }

      try {
        const tickets = await api.call('/api/v1/user/ticket/fetch', 'GET')
        if (!cancelled) {
          const open = Array.isArray(tickets)
            ? tickets.filter((t) => t?.status === 0).length
            : 0
          setOpenTicketCount(open)
        }
      } catch (e) {
        // silently ignore
      }
    }

    fetchCounts()
    return () => {
      cancelled = true
    }
  }, [user?.token, user?.baseUrl])

  const avatarUrl = useMemo(
    () =>
      user?.avatar_url ||
      `https://i.pravatar.cc/150?u=${user?.email || 'v2board'}`,
    [user?.avatar_url, user?.email],
  )

  const fallbackInitial = useMemo(
    () => user?.email?.[0]?.toUpperCase() ?? 'V',
    [user?.email],
  )

  const handleMenuAction = useCallback(
    (key) => {
      switch (key) {
        case 'profile':
          navigate('/profile')
          break
        case 'plans':
          navigate('/plans')
          break
        case 'servers':
          navigate('/servers')
          break
        case 'traffic':
          navigate('/traffic')
          break
        case 'docs':
          navigate('/docs')
          break
        case 'invite':
          navigate('/invite')
          break
        case 'tickets':
          navigate('/tickets')
          break
        case 'help':
          navigate('/tickets/create')
          break
        case 'logout':
          openLogoutConfirm()
          break
        default:
          break
      }
    },
    [navigate, openLogoutConfirm],
  )

  return (
    <>
      <header
        className={cn(
          'w-full transition-all duration-200 pt-safe pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] lg:px-6',
          isScrolled
            ? 'bg-content1/95 backdrop-blur-lg shadow-sm border-b border-default-200'
            : 'bg-content1/80 backdrop-blur-md',
        )}
      >
        <div className="flex h-[72px] items-center justify-between gap-4">
          {/* 左侧留白：移动端无侧边栏，桌面端 Sidebar 有自己 logo */}
          <div className="flex items-center gap-3" />

          {/* 右侧操作 */}
          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              variant="ghost"
              size="md"
              onPress={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="min-w-11 min-h-11 text-default-500 hover:text-default-foreground"
            >
              {isDark ? (
                <Sun className="w-5 h-5" strokeWidth={2.5} />
              ) : (
                <Moon className="w-5 h-5" strokeWidth={2.5} />
              )}
            </Button>

            <Badge.Anchor>
              <Button
                isIconOnly
                variant="ghost"
                size="md"
                onPress={() => navigate('/notifications')}
                aria-label="查看通知"
                className="min-w-11 min-h-11 text-default-500 hover:text-default-foreground"
              >
                <Bell className="w-5 h-5" strokeWidth={2.5} />
              </Button>
              {noticeCount > 0 && (
                <Badge color="danger" size="sm">
                  {noticeCount > 99 ? '99+' : noticeCount}
                </Badge>
              )}
            </Badge.Anchor>

            <div className="h-8 w-px bg-default-200" />

            <Dropdown>
              <DropdownTrigger
                aria-label="Open user menu"
                className="rounded-full outline-none data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-primary data-[focus-visible=true]:ring-offset-2 data-[focus-visible=true]:ring-offset-background"
              >
                <Badge.Anchor>
                  <Avatar size="md">
                    <Avatar.Image src={avatarUrl} alt={user?.email || 'user'} />
                    <Avatar.Fallback>{fallbackInitial}</Avatar.Fallback>
                  </Avatar>
                  <Badge placement="bottom-right" size="sm" color="warning">
                    <Crown className="w-3 h-3" strokeWidth={3} />
                  </Badge>
                </Badge.Anchor>
              </DropdownTrigger>

              <DropdownPopover
                placement="bottom end"
                className="max-w-[calc(100vw-1rem)]"
              >
                <DropdownMenu aria-label="用户操作" onAction={handleMenuAction}>
                  <DropdownItem
                    id="profile-header"
                    textValue="用户资料"
                    isDisabled
                    className="h-14 gap-2 data-[disabled=true]:opacity-100 cursor-default"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="font-semibold text-sm text-default-600">
                        已登录账号
                      </p>
                      <p className="font-semibold text-sm">
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                  </DropdownItem>

                  <DropdownItem id="profile" textValue="个人资料">
                    <User className="w-4 h-4" />
                    个人资料
                  </DropdownItem>

                  {/* 移动端补全的次级入口 */}
                  <DropdownItem id="plans" textValue="订阅套餐" className="lg:hidden">
                    <ShoppingCart className="w-4 h-4" />
                    订阅套餐
                  </DropdownItem>
                  <DropdownItem id="servers" textValue="节点列表" className="lg:hidden">
                    <Server className="w-4 h-4" />
                    节点列表
                  </DropdownItem>
                  <DropdownItem id="traffic" textValue="流量统计" className="lg:hidden">
                    <BarChart3 className="w-4 h-4" />
                    流量统计
                  </DropdownItem>
                  <DropdownItem id="docs" textValue="文档中心" className="lg:hidden">
                    <BookOpen className="w-4 h-4" />
                    文档中心
                  </DropdownItem>
                  <DropdownItem id="invite" textValue="推广返利" className="lg:hidden">
                    <Gift className="w-4 h-4" />
                    推广返利
                  </DropdownItem>

                  <DropdownItem id="tickets" textValue="我的工单">
                    <Shield className="w-4 h-4" />
                    <span className="flex-1">我的工单</span>
                    {openTicketCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-danger text-danger-foreground text-[10px] font-bold leading-none">
                        {openTicketCount > 99 ? '99+' : openTicketCount}
                      </span>
                    )}
                  </DropdownItem>
                  <DropdownItem id="help" textValue="提交工单">
                    <Settings className="w-4 h-4" />
                    提交工单
                  </DropdownItem>

                  <DropdownItem
                    id="logout"
                    textValue="退出登录"
                    variant="danger"
                    className="text-danger"
                  >
                    <LogOut className="w-4 h-4" />
                    退出登录
                  </DropdownItem>
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          </div>
        </div>
      </header>

      <MobileSheetModal
        isOpen={isLogoutConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !isLoggingOut) closeLogoutConfirm()
        }}
        variant="blur"
        size="sm"
      >
            <ModalHeader className="flex items-center gap-2">
              <ModalHeading className="sr-only">Logout confirmation</ModalHeading>
              <AlertTriangle className="w-5 h-5 text-danger" />
              确认退出登录
            </ModalHeader>
            <ModalBody>
              <div className="p-4 rounded-xl bg-danger-50 dark:bg-danger-900/20 border border-danger-100 dark:border-danger-800 text-danger-600 dark:text-danger-400 flex gap-3">
                <LogOut className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold mb-1">退出后需要重新登录</p>
                  <p>你将被引导回登录页面，账户的本地会话将被清除。</p>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                onPress={closeLogoutConfirm}
                isDisabled={isLoggingOut}
              >
                取消
              </Button>
              <Button
                variant="danger"
                onPress={handleLogout}
                isDisabled={isLoggingOut}
              >
                {isLoggingOut ? '退出中…' : '确认退出'}
              </Button>
            </ModalFooter>
      </MobileSheetModal>
    </>
  )
}

export default Header
